import { useEffect } from 'react';
import { EditModeState } from '@/features/editMode/enums/EditModeState';
import { EditModeStatus } from '@/features/editMode/enums/EditModeStatus';
import EditModeEvents from '@/features/editMode/events';
import { changeEditModeStatus, closeEditMode } from '@/features/editMode/store/editModeSlice';
import { useServerNotificationEvents } from '@/features/notifications/hooks/useServerNotificationEvents';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { logout } from '@/domains/auth/store/authSlice';
import { selectUser } from '@/domains/user/store/selectors';
import { useEventEffect } from '@/hooks/useEventEffect';
import EventService from '@/services/EventService/EventService';
import ServerEventService from '@/services/ServerEventService/ServerEventService';
import { ServerSideEvents } from '@/services/ServerEventService/events';
import SnackService from '@/services/SnackService/SnackService';
import { useDispatch, useSelector } from '@/store/hooks';
import { convertServerSideMessageToBoldContent } from '@/utils/converters/convertServerSideMessageToContent';

export const useServerSideEvents = (hubUrl: string) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  useServerNotificationEvents(!user);

  useEffect(() => {
    if (user) ServerEventService.createConnection(hubUrl);
  }, [user, hubUrl]);

  useEventEffect(ServerSideEvents.DisabledUser, (data) => {
    SnackService.add({
      type: SnackType.Error,
      content: data.message,
    });
    dispatch(logout());
  });

  useEventEffect(ServerSideEvents.EnabledUser, (data) => {
    SnackService.add({
      type: SnackType.Info,
      content: convertServerSideMessageToBoldContent(data.message, data.payload),
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
    dispatch(logout());
  });

  useEventEffect(ServerSideEvents.ChangedUserRole, (data) => {
    SnackService.add({
      type: SnackType.Info,
      title: 'Your security role has been changed',
      content: convertServerSideMessageToBoldContent(data.message, data.payload),
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
    dispatch(logout());
  });

  useEventEffect(ServerSideEvents.ChangedUserStatus, (data) => {
    SnackService.add({
      type: SnackType.Info,
      content: convertServerSideMessageToBoldContent(data.message, data.payload),
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
    dispatch(logout());
  });

  useEventEffect(ServerSideEvents.EnableEditMode, () => {
    dispatch(changeEditModeStatus(EditModeStatus.Enabled));
    EventService.dispatch(new EditModeEvents.Allowed({}));
  });

  useEventEffect(ServerSideEvents.DisabledEditMode, (data) => {
    dispatch(closeEditMode({ newState: EditModeState.Disabled, newStatus: EditModeStatus.Blocked }));
    EventService.dispatch(new EditModeEvents.Blocked({}));

    SnackService.add({
      type: SnackType.Error,
      title: data.message,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
  });
};
